/**
 * Merchant brand merge matching for Wallet Admin.
 * Never deletes CloudKit record names — losers become inactive aliases.
 */

import type { MerchantIcon, WalletCategory } from "./catalogTypes";
import {
  encodeCountryScope,
  encodeMatchTerms,
  merchantSourceSlug,
  normalizeMerchantName,
  parseCountryScopeJSON,
  parseMatchTermsJSON,
} from "./catalogTypes";

export type MerchantDraft = Omit<MerchantIcon, "recordName"> & {
  recordName?: string;
  matchTerms?: string[];
};

export type MergeReason =
  | "normalizedName"
  | "wikidata"
  | "nsiId"
  | "alias"
  | "similar";

export type MergePair = {
  id: string;
  left: MerchantDraft;
  right: MerchantDraft;
  reason: MergeReason;
  exact: boolean;
  warnings: string[];
};

const STOP_TOKENS = new Set([
  "the",
  "and",
  "of",
  "inc",
  "llc",
  "ltd",
  "co",
  "corp",
  "corporation",
  "company",
  "foods",
  "group",
  "ph",
  "philippines",
  "international",
  "store",
  "stores",
]);

const FOOD_SPECIFICITY = [
  "amenity/fast_food",
  "amenity/cafe",
  "amenity/food_court",
  "shop/bakery",
  "amenity/ice_cream",
  "amenity/bar",
  "amenity/pub",
  "amenity/restaurant",
];

function tokens(name: string): Set<string> {
  return new Set(
    normalizeMerchantName(name)
      .split(/\s+/)
      .filter((token) => token.length > 1 && !STOP_TOKENS.has(token)),
  );
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const token of a) if (b.has(token)) intersection += 1;
  return intersection / (a.size + b.size - intersection);
}

function isSubset(small: Set<string>, large: Set<string>): boolean {
  if (small.size === 0) return false;
  for (const token of small) if (!large.has(token)) return false;
  return true;
}

function aliasSet(row: MerchantDraft): Set<string> {
  const terms = [
    normalizeMerchantName(row.displayName),
    normalizeMerchantName(row.normalizedName),
    ...parseMatchTermsJSON(row.matchTermsJSON).map((term) => normalizeMerchantName(term)),
    ...(row.matchTerms ?? []).map((term) => normalizeMerchantName(term)),
  ].filter(Boolean);
  return new Set(terms);
}

function aliasesOverlap(left: MerchantDraft, right: MerchantDraft): boolean {
  const a = aliasSet(left);
  const b = aliasSet(right);
  for (const term of a) {
    if (term && b.has(term)) return true;
  }
  return false;
}

export function isSimilarName(left: string, right: string): boolean {
  const a = tokens(left);
  const b = tokens(right);
  if (a.size === 0 || b.size === 0) return false;
  if (isSubset(a, b) || isSubset(b, a)) return true;
  return jaccard(a, b) >= 0.5;
}

function exactReason(left: MerchantDraft, right: MerchantDraft): MergeReason | null {
  if (
    normalizeMerchantName(left.normalizedName) &&
    normalizeMerchantName(left.normalizedName) === normalizeMerchantName(right.normalizedName)
  ) {
    return "normalizedName";
  }
  if (left.wikidataId && right.wikidataId && left.wikidataId === right.wikidataId) {
    return "wikidata";
  }
  if (left.nsiId && right.nsiId && left.nsiId === right.nsiId) return "nsiId";
  if (aliasesOverlap(left, right)) return "alias";
  return null;
}

function similarMatch(left: MerchantDraft, right: MerchantDraft): boolean {
  if (left.walletCategory !== right.walletCategory) return false;
  return (
    isSimilarName(left.displayName, right.displayName) ||
    isSimilarName(left.normalizedName, right.normalizedName) ||
    isSimilarName(left.displayName, right.normalizedName) ||
    isSimilarName(left.normalizedName, right.displayName)
  );
}

function pairId(left: MerchantDraft, right: MerchantDraft, reason: MergeReason): string {
  const a = left.recordName || left.nsiId || left.normalizedName;
  const b = right.recordName || right.nsiId || right.normalizedName;
  return `${reason}:${a}::${b}`;
}

function preferExistingLeft(existing: MerchantDraft, incoming: MerchantDraft): {
  left: MerchantDraft;
  right: MerchantDraft;
} {
  if (existing.recordName && !incoming.recordName) return { left: existing, right: incoming };
  return { left: existing, right: incoming };
}

/**
 * Greedy match of incoming drafts against existing catalog rows, then leftover
 * incoming drafts against each other.
 */
export function buildMergeQueue(
  existing: readonly MerchantDraft[],
  incoming: readonly MerchantDraft[],
): { newDrafts: MerchantDraft[]; exact: MergePair[]; similar: MergePair[] } {
  const usedExisting = new Set<number>();
  const usedIncoming = new Set<number>();
  const exact: MergePair[] = [];
  const similar: MergePair[] = [];

  const pushPair = (
    left: MerchantDraft,
    right: MerchantDraft,
    reason: MergeReason,
    exactMatch: boolean,
  ) => {
    const pair: MergePair = {
      id: pairId(left, right, reason),
      ...preferExistingLeft(left, right),
      reason,
      exact: exactMatch,
      warnings: [],
    };
    if (exactMatch) exact.push(pair);
    else similar.push(pair);
  };

  for (let i = 0; i < incoming.length; i++) {
    const draft = incoming[i]!;
    let bestExact: { index: number; reason: MergeReason } | null = null;
    let bestSimilar: number | null = null;
    for (let e = 0; e < existing.length; e++) {
      if (usedExisting.has(e)) continue;
      const row = existing[e]!;
      if (row.mergedIntoRecordName) continue;
      const reason = exactReason(row, draft);
      if (reason) {
        bestExact = { index: e, reason };
        break;
      }
      if (bestSimilar == null && similarMatch(row, draft)) bestSimilar = e;
    }
    if (bestExact) {
      usedIncoming.add(i);
      usedExisting.add(bestExact.index);
      pushPair(existing[bestExact.index]!, draft, bestExact.reason, true);
      continue;
    }
    if (bestSimilar != null) {
      usedIncoming.add(i);
      usedExisting.add(bestSimilar);
      pushPair(existing[bestSimilar]!, draft, "similar", false);
    }
  }

  for (let i = 0; i < incoming.length; i++) {
    if (usedIncoming.has(i)) continue;
    for (let j = i + 1; j < incoming.length; j++) {
      if (usedIncoming.has(j)) continue;
      const left = incoming[i]!;
      const right = incoming[j]!;
      const reason = exactReason(left, right);
      if (reason) {
        usedIncoming.add(i);
        usedIncoming.add(j);
        pushPair(left, right, reason, true);
        break;
      }
      if (similarMatch(left, right)) {
        usedIncoming.add(i);
        usedIncoming.add(j);
        pushPair(left, right, "similar", false);
        break;
      }
    }
  }

  const newDrafts = incoming.filter((_, index) => !usedIncoming.has(index));
  return { newDrafts, exact, similar };
}

function betterDisplayName(left: string, right: string): string {
  const a = left.trim();
  const b = right.trim();
  if (!a) return b;
  if (!b) return a;
  const aNorm = normalizeMerchantName(a);
  const bNorm = normalizeMerchantName(b);
  if (aNorm === bNorm) return a.length <= b.length ? a : b;
  if (bNorm.startsWith(aNorm) && a.length < b.length) return a;
  if (aNorm.startsWith(bNorm) && b.length < a.length) return b;
  return a;
}

export function moreSpecificGranularKind(
  survivor: string,
  other: string,
): { kind: string; warning: string | null } {
  if (!other || other === survivor) return { kind: survivor || other, warning: null };
  if (!survivor) return { kind: other, warning: null };
  const survivorCms = survivor.startsWith("cms/");
  const otherOsm = other.includes("/") && !other.startsWith("cms/");
  if (survivorCms && otherOsm) return { kind: other, warning: null };
  const a = FOOD_SPECIFICITY.indexOf(survivor);
  const b = FOOD_SPECIFICITY.indexOf(other);
  if (a >= 0 && b >= 0) {
    return a <= b
      ? { kind: survivor, warning: null }
      : { kind: other, warning: null };
  }
  if (survivor !== other) {
    return {
      kind: survivor,
      warning: `Kept granular kind ${survivor} (also saw ${other}).`,
    };
  }
  return { kind: survivor, warning: null };
}

export function mergeMerchantDrafts(
  survivor: MerchantDraft,
  loser: MerchantDraft,
): { survivor: MerchantDraft; loser: MerchantDraft; warnings: string[] } {
  const warnings: string[] = [];
  const survivorTerms = [
    ...parseMatchTermsJSON(survivor.matchTermsJSON),
    ...(survivor.matchTerms ?? []),
  ];
  const loserTerms = [
    ...parseMatchTermsJSON(loser.matchTermsJSON),
    ...(loser.matchTerms ?? []),
    loser.displayName,
    loser.normalizedName,
  ];
  const matchTerms = Array.from(
    new Set(
      [...survivorTerms, ...loserTerms]
        .map((term) => term.trim().toLowerCase())
        .filter((term) => term && term !== normalizeMerchantName(survivor.displayName)),
    ),
  );

  let walletCategory: WalletCategory = survivor.walletCategory;
  if (survivor.walletCategory !== loser.walletCategory) {
    warnings.push(
      `Wallet category conflict: kept ${survivor.walletCategory} (also ${loser.walletCategory}).`,
    );
  }

  const granular = moreSpecificGranularKind(survivor.granularKind, loser.granularKind);
  if (granular.warning) warnings.push(granular.warning);

  const countries = encodeCountryScope([
    ...parseCountryScopeJSON(survivor.countryScopeJSON),
    ...parseCountryScopeJSON(loser.countryScopeJSON),
  ]);

  const sourceSlug =
    survivor.sourceSlug ||
    loser.sourceSlug ||
    merchantSourceSlug(survivor.nsiId || survivor.normalizedName);

  const mergedSurvivor: MerchantDraft = {
    ...survivor,
    displayName: betterDisplayName(survivor.displayName, loser.displayName),
    normalizedName:
      normalizeMerchantName(survivor.normalizedName) ||
      normalizeMerchantName(loser.normalizedName),
    matchTerms,
    matchTermsJSON: encodeMatchTerms(matchTerms),
    sourceSlug,
    sourceName: survivor.sourceName || loser.sourceName,
    brandHex: survivor.brandHex || loser.brandHex,
    iconURL: survivor.iconURL || loser.iconURL,
    walletCategory,
    granularKind: granular.kind,
    countryScopeJSON: countries,
    wikidataId: survivor.wikidataId || loser.wikidataId,
    nsiId: survivor.nsiId || loser.nsiId,
    sourceKind: survivor.iconURL ? survivor.sourceKind : survivor.sourceKind || loser.sourceKind,
    mergedIntoRecordName: null,
    isActive: true,
    sortOrder: survivor.sortOrder || loser.sortOrder,
    categoryHint: survivor.categoryHint,
    recordName: survivor.recordName ?? `merchant-${merchantSourceSlug(sourceSlug)}`,
  };

  const loserSlug =
    loser.sourceSlug ||
    merchantSourceSlug(loser.nsiId || loser.normalizedName) ||
    `${sourceSlug}-alias`;
  const mergedLoser: MerchantDraft = {
    ...loser,
    sourceSlug: loserSlug,
    matchTerms: parseMatchTermsJSON(loser.matchTermsJSON),
    isActive: false,
    mergedIntoRecordName: mergedSurvivor.recordName ?? `merchant-${merchantSourceSlug(sourceSlug)}`,
    recordName: loser.recordName ?? `merchant-${loserSlug}`,
  };

  return { survivor: mergedSurvivor, loser: mergedLoser, warnings };
}

export function merchantToSeedRow(row: MerchantDraft): NonNullable<
  import("./catalogTypes").SeedPayload["merchantIcons"]
>[number] {
  return {
    recordName: row.recordName,
    displayName: row.displayName,
    normalizedName: row.normalizedName,
    matchTerms: row.matchTerms ?? parseMatchTermsJSON(row.matchTermsJSON),
    matchTermsJSON: row.matchTermsJSON,
    sourceSlug: row.sourceSlug,
    sourceName: row.sourceName,
    brandHex: row.brandHex,
    iconURL: row.iconURL,
    categoryHint: row.categoryHint,
    walletCategory: row.walletCategory,
    granularKind: row.granularKind,
    countryScopeJSON: row.countryScopeJSON,
    wikidataId: row.wikidataId,
    nsiId: row.nsiId,
    sourceKind: row.sourceKind,
    mergedIntoRecordName: row.mergedIntoRecordName,
    isActive: row.isActive,
    sortOrder: row.sortOrder,
  };
}
